import React, { useContext, useEffect, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Container,
  Grid2 as Grid,
  Grid2Props as GridProps,
} from "@mui/material";

import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  FileButton,
  ActionIcon,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronRight,
  IconDownload,
  IconEye,
  IconFile,
  IconFiles,
  IconNotebook,
  IconPlayerPlay,
  IconPlus,
  IconReload,
  IconTerminal2,
  IconUserCircle,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Colors, HttpErrorCodes } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getEllipsisFromString,
} from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  CourseProps,
  LessonProps,
  MaterialProps,
  PaginationProps,
} from "../../Lib/types";
import {
  PerformRequest,
  UploadFiles,
  usePerformRequest,
} from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";
import "./styles.scss";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { setCommentRange } from "typescript";

const CourseMaterials: React.FC = () => {
  const context = useContext(AppContext);
  const { lessonID } = useParams();
  const navigate = useNavigate();

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 10,
  });

  const [
    isLessonModalOpen,
    { close: CloseLessonModal, open: OpenLessonModal },
  ] = useDisclosure(false);

  const [modalFormAction, setModalFormAction] = useState<
    "create" | "update" | "view"
  >("create");

  const [tempFiles, setTempFiles] = useState<File[]>([]);
  const materialForm = useForm({
    initialValues: {
      id: "",
      title: "",
      description: "",
      files: [],
      createdAt: "",
      updatedAt: "",
    },
  });
  const [currentMaterial, setCurrentMaterial] = useState<MaterialProps>();

  const {
    data: materials,
    isLoading: isLoadingMaterials,
    reloadData: reloadMaterials,
    response: materialsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<MaterialProps[]>>({
    method: Endpoints.GetLessonMaterials(
      lessonID as string,
      pagination.page,
      pagination.limit
    ).method,
    route: Endpoints.GetLessonMaterials(
      lessonID as string,
      pagination.page,
      pagination.limit
    ).route,
  });
  const {
    data: lesson,
    isLoading: isLoadingLesson,
    reloadData: reloadLesson,
    response: lessonAxiosResponse,
  } = usePerformRequest<NonPaginatedResponse<LessonProps>>({
    method: Endpoints.GetSingleLesson(lessonID as string).method,
    route: Endpoints.GetSingleLesson(lessonID as string).route,
  });
  useEffect(() => {
    if (materialsAxiosResponse) {
      if (HttpErrorCodes.includes(materialsAxiosResponse.status)) {
        DefaultErrorNotification("Could not get materials!");
        navigate(-1);
      }
    }
  }, [isLoadingMaterials, materialsAxiosResponse]);

  const [isCreatingOrUpdatingMaterial, setCreatingOrUpdatingMaterial] =
    useState<boolean>(false);
  const CreateMaterial = async () => {
    setCreatingOrUpdatingMaterial(true);
    UploadFiles(tempFiles as any, true)
      .catch(() => {
        setCreatingOrUpdatingMaterial(false);
        DefaultErrorNotification("Could not upload image!");
      })
      .then(async (uploaded) => {
        setCreatingOrUpdatingMaterial(false);
        if (uploaded) {
          if (uploaded.status === 201) {
            // FILE WAS UPLOADED SUCCESSFULLY!
            setCreatingOrUpdatingMaterial(true);
            const r = await PerformRequest({
              method: Endpoints.GetCreateMaterial(lessonID as string).method,
              route: Endpoints.GetCreateMaterial(lessonID as string).route,
              data: {
                title: materialForm.values.title,
                description: materialForm.values.description,
                files: uploaded.data.map((file) => file.path),
              },
              callback: () => {
                setCreatingOrUpdatingMaterial(false);
              },
            });
            setCreatingOrUpdatingMaterial(false);
            if (r) {
              if (r.status === 201) {
                // MATERIAL WAS CREATED SUCCESSFULLY!
                DefaultSuccessNotification("Material created successfully!");
                reloadMaterials();
                CloseLessonModal();
                materialForm.reset();
              } else {
                DefaultErrorNotification("Could not create material!");
              }
            }
          } else {
            DefaultErrorNotification("Could not upload image!");
          }
        }
      });
  };
  const CancelCreateMaterial = () => {
    CloseLessonModal();
    setCurrentMaterial(undefined);
    setModalFormAction("create");
    materialForm.reset();
    setTempFiles([]);
  };
  const gridProps: GridProps = {
    size: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 4,
      xl: 4,
    },
  };

  return (
    <Container maxWidth="lg">
      <div className="single-course-container">
        {isLoadingMaterials ? (
          <MegaLoader />
        ) : (
          <>
            <Text mb={-4} fz="xs" c="dimmed">
              Lesson Title
            </Text>
            <Text fw={700} fz={21} c={Colors.PurplePrimary}>
              <div className="flex-row align-center">
                <IconChevronRight
                  size={19}
                  color={Colors.PurplePrimary}
                  strokeWidth={3}
                />
                {lesson?.data.title}
              </div>
            </Text>
            <Text mb={10} maw={1200} fz={16}>
              {lesson?.data.description}
            </Text>
            <Divider my={20} />
            <div className="flex-row align-center">
              <Link to={`/d/course/${lesson?.data?.courseID}`}>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color={Colors.PurplePrimary}
                >
                  Back to Course
                </Button>
              </Link>
              <Link to={`/d/lesson/assessments/${lessonID}`}>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color={Colors.PurplePrimary}
                >
                  Lesson Assessments
                </Button>
              </Link>
            </div>
            <Divider my={20} />
            <Tabs
              mt={25}
              defaultValue="materials"
              variant="default"
              color={Colors.PurplePrimary}
            >
              <Tabs.List grow>
                <Tabs.Tab
                  value="materials"
                  leftSection={<IconFiles size={19} />}
                >
                  Materials
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="materials">
                <div className="flex-row align-center justify-between width-100">
                  <div className="flex-row align-center">
                    <IconChevronRight size={19} color={Colors.PurplePrimary} />
                    <Text fw={600} my={10} c={Colors.PurplePrimary}>
                      Materials
                    </Text>
                  </div>
                  <div className="flex-row align-center">
                    <ActionIcon
                      variant="filled"
                      size="lg"
                      disabled={isLoadingMaterials}
                      color={Colors.PurplePrimary}
                      onClick={() => {
                        OpenLessonModal();
                        setModalFormAction("create");
                        materialForm.reset();
                      }}
                    >
                      <IconPlus size={18} />
                    </ActionIcon>
                    <ActionIcon
                      variant="outline"
                      size="lg"
                      ml={10}
                      loading={isLoadingMaterials}
                      color={Colors.PurplePrimary}
                      onClick={() => {
                        reloadMaterials();
                      }}
                    >
                      <IconReload size={18} />
                    </ActionIcon>
                  </div>
                </div>
                <div className="flex-col width-100">
                  {isLoadingMaterials ? (
                    <MegaLoader />
                  ) : (
                    <>
                      {materials &&
                      materials.data &&
                      materials.data.length > 0 ? (
                        <Grid container spacing={2}>
                          {materials.data.map((material) => (
                            <Grid {...gridProps} key={material.id}>
                              <Card
                                key={material.id}
                                shadow="sm"
                                padding="lg"
                                radius="md"
                                withBorder
                                mt="sm"
                                h={250}
                                className="lesson-card"
                              >
                                <Group justify="space-between" mb="xs">
                                  <Text
                                    fw={500}
                                    fz={15}
                                    c={Colors.PurplePrimary}
                                  >
                                    {material.title}
                                  </Text>
                                  <Badge
                                    color={Colors.PurplePrimary}
                                    variant="light"
                                  >
                                    {dayjs(material.createdAt).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </Badge>
                                </Group>

                                <Text fz={13} c={Colors.GrayPrimary}>
                                  {getEllipsisFromString(
                                    material.description,
                                    200
                                  )}
                                </Text>

                                <Button
                                  variant="outline"
                                  color={Colors.PurplePrimary}
                                  fullWidth
                                  mt="md"
                                  radius="sm"
                                  leftSection={<IconEye size={16} />}
                                  onClick={() => {
                                    OpenLessonModal();
                                    setCurrentMaterial(material);
                                    setModalFormAction("view");
                                  }}
                                >
                                  View Details
                                </Button>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <DisplayAlert
                          title="No materials found"
                          children={
                            <div className="flex-col align-start">
                              <Text>
                                No materials found for this course. You can add
                                a new material by clicking the button below.
                              </Text>
                              <Button
                                color={Colors.PurplePrimary}
                                leftSection={<IconPlus size={16} />}
                                onClick={() => {
                                  OpenLessonModal();
                                  setModalFormAction("create");
                                }}
                                w={160}
                                mt={10}
                              >
                                Add Material
                              </Button>
                            </div>
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </div>
      <Modal
        centered
        title={
          modalFormAction === "create" ? "Create Material" : "Update Material"
        }
        opened={isLessonModalOpen}
        size="lg"
        onClose={() => {
          if (isCreatingOrUpdatingMaterial) {
            return;
          } else {
            CancelCreateMaterial();
          }
        }}
      >
        {currentMaterial && modalFormAction === "view" && (
          <>
            <Table variant="vertical" layout="fixed" withTableBorder>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th w={160}>Title</Table.Th>
                  <Table.Td>{currentMaterial.title}</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Description</Table.Th>
                  <Table.Td>
                    {getEllipsisFromString(currentMaterial.description, 100)}
                  </Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Created at</Table.Th>
                  <Table.Td>
                    {dayjs(currentMaterial.createdAt).format("DD/MM/YYYY")}{" "}
                    {dayjs(currentMaterial.createdAt).format("hh:mmA")}
                  </Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Last updated at</Table.Th>
                  <Table.Td>
                    {dayjs(currentMaterial.createdAt).format("DD/MM/YYYY")}{" "}
                    {dayjs(currentMaterial.createdAt).format("hh:mmA")}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            <Divider my={10} />
          </>
        )}
        {modalFormAction !== "view" && (
          <div className="flex-col">
            <TextInput
              label="Title"
              placeholder="Enter material title"
              {...materialForm.getInputProps("title")}
            />
            <Textarea
              label="Description"
              placeholder="Enter material description"
              {...materialForm.getInputProps("description")}
              rows={4}
              resize="vertical"
            />

            <Dropzone
              onDrop={(files: File[]) => {
                setTempFiles((prev) => [...prev, ...files]);
                notifications.show({
                  title: "Files added",
                  message: `${files.length} file(s) have been added`,
                  color: "green",
                });
              }}
              maxSize={5 * 1024 ** 2}
              accept={{
                "application/pdf": [".pdf"],
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                  [".docx"],
                "application/vnd.openxmlformats-officedocument.presentationml.presentation":
                  [".ppt", ".pptx"],
                "text/plain": [".txt"],
                "image/png": [".png"],
                "image/jpeg": [".jpg", ".jpeg"],
              }}
              mt={10}
            >
              <Group
                justify="center"
                gap="xl"
                style={{ minHeight: 100, pointerEvents: "none" }}
              >
                <Dropzone.Accept>
                  <IconFiles
                    size={32}
                    stroke={1.5}
                    color={Colors.GreenPrimary}
                  />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={32} stroke={1.5} color="red" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFiles size={32} stroke={1.5} />
                </Dropzone.Idle>

                <div>
                  <Text size="xl" inline>
                    Drag files here or click to select files
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={7}>
                    Attach as many files as you like, each file should not
                    exceed 5mb
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={3}>
                    Accepted files: PDF, DOCX, PPT, TXT, PNG, JPEG, JPG
                  </Text>
                </div>
              </Group>
            </Dropzone>

            {tempFiles.length > 0 && (
              <div className="flex-col" style={{ marginTop: "10px" }}>
                <Text size="sm" fw={500}>
                  Selected files:
                </Text>
                {tempFiles.map((file, index) => (
                  <Group key={index} mt={5}>
                    <IconFile size={16} />
                    <Text size="sm">{file.name}</Text>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => {
                        setTempFiles((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                      }}
                    >
                      Remove
                    </Button>
                  </Group>
                ))}
              </div>
            )}

            <Button
              type="submit"
              mt={10}
              color={Colors.PurplePrimary}
              loading={isCreatingOrUpdatingMaterial}
              onClick={CreateMaterial}
            >
              {modalFormAction === "create"
                ? "Create Material"
                : "Update Material"}
            </Button>
          </div>
        )}
        {modalFormAction === "view" && (
          <div className="flex-col">
            <Group justify="space-between" mb="xs">
              <Text fw={600} fz={20} c={Colors.PurplePrimary}>
                {currentMaterial?.title}
              </Text>
              <Badge color={Colors.PurplePrimary} variant="light">
                {dayjs(currentMaterial?.createdAt).format("DD/MM/YYYY")}
              </Badge>
            </Group>

            <Text fz={15} c={Colors.GrayPrimary} mb={20}>
              {currentMaterial?.description}
            </Text>

            <Divider my={15} />

            <Text fw={500} mb={10}>
              Attached Files:
            </Text>

            {currentMaterial?.files && currentMaterial.files.length > 0 ? (
              <div className="flex-col">
                {currentMaterial.files.map((file, index) => (
                  <Group key={index} mt={8}>
                    <IconFile size={18} />
                    <Text size="sm" style={{ flex: 1 }}>
                      {file.split("/").pop()}
                    </Text>
                    <Button
                      variant="light"
                      size="xs"
                      color={Colors.PurplePrimary}
                      leftSection={<IconDownload size={14} />}
                      component="a"
                      href={file}
                      target="_blank"
                      // onClick={() => {
                      //   console.log({ file, currentMaterial });
                      // }}
                    >
                      Download
                    </Button>
                  </Group>
                ))}
              </div>
            ) : (
              <Text c="dimmed" fz="sm" ta="center" py={20}>
                No files attached to this material
              </Text>
            )}

            <Button
              color={Colors.PurplePrimary}
              mt={20}
              fullWidth
              onClick={CancelCreateMaterial}
            >
              Close
            </Button>
          </div>
        )}
      </Modal>
    </Container>
  );
};

export default CourseMaterials;
